import { Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { AtendimentoService } from '../../../../../services/atendimento/atendimento.service';
import { ContaPacienteResumo, ItemContaPaciente } from '../../../../../interfaces/dados-pagamento';
import { BrlPipe } from '../../../../../utils/brl.pipe';
import { contasDaJanela, filtrarContasPorStatus, FiltroStatusConta } from '../../../../../utils/historico-paciente.util';

const DESTAQUE_MS = 2500;

function hojeLocalIso(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

@Component({
  selector: 'app-hp-contas',
  standalone: true,
  imports: [CommonModule, BrlPipe],
  templateUrl: './hp-contas.component.html',
  styleUrl: './hp-contas.component.scss',
})
export class HpContasComponent implements OnChanges, OnDestroy {
  @Input() contas: ContaPacienteResumo[] = [];
  @Input() supervisor = false;
  /** nrAtendimento da conta a rolar, abrir e destacar. */
  @Input() destacar: number | null = null;
  @Input() aviso = '';
  @Output() pagar = new EventEmitter<ContaPacienteResumo>();
  @Output() vincular = new EventEmitter<ContaPacienteResumo>();
  @Output() menu = new EventEmitter<{ ev: MouseEvent; conta: ContaPacienteResumo }>();
  /** Os chips ficam no cabeçalho fixo do Histórico; aqui só se aplica o filtro. */
  @Input() filtro: FiltroStatusConta = 'todos';
  @Output() filtroChange = new EventEmitter<FiltroStatusConta>();
  mostrarOcultas = false;
  naoEncontrada = false;
  contaDestacada: number | null = null;
  abertas = new Set<number>();
  itens = new Map<number, ItemContaPaciente[]>();
  carregando = new Set<number>();
  falhou = new Set<number>();
  private timer: ReturnType<typeof setTimeout> | null = null;
  // recarga das contas (pagamento) nao pode reabrir/rolar de novo um destaque ja aplicado
  private destaquePendente = false;

  constructor(private atendimentoService: AtendimentoService, private host: ElementRef<HTMLElement>) {}

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['contas'] && !ch['contas'].firstChange) this.recarregarItensAbertos();
    if (ch['destacar']) this.destaquePendente = this.destacar != null;
    if (ch['destacar'] || (ch['contas'] && this.destaquePendente)) this.aplicarDestaque();
  }

  ngOnDestroy(): void {
    if (this.timer) clearTimeout(this.timer);
  }

  private get janela() {
    return contasDaJanela(this.contas ?? [], hojeLocalIso());
  }

  get qtOcultas(): number {
    return this.janela.ocultas.length;
  }

  /** Contas na janela, mantendo a ordem original. */
  get base(): ContaPacienteResumo[] {
    const { ocultas } = this.janela;
    if (this.mostrarOcultas || !ocultas.length) return this.contas ?? [];
    const fora = new Set(ocultas);
    return (this.contas ?? []).filter(c => !fora.has(c));
  }

  /** A janela de 40 dias vale só para "Todas"; os outros chips olham todas as contas carregadas. */
  get exibidas(): ContaPacienteResumo[] {
    return this.filtro === 'todos' ? this.base : filtrarContasPorStatus(this.contas ?? [], this.filtro);
  }

  saldo(c: ContaPacienteResumo): number {
    return c.vlConta - c.vlPago;
  }

  definitiva(c: ContaPacienteResumo): boolean {
    return !!c.statusConta?.toLowerCase().includes('definit');
  }

  async alternar(c: ContaPacienteResumo): Promise<void> {
    const k = c.nrInternoConta;
    if (this.abertas.has(k)) { this.abertas.delete(k); return; }
    this.abertas.add(k);
    await this.carregarItens(c);
  }

  // contas recarregadas (pagamento, ação de supervisor): os itens em cache podem ter mudado
  private recarregarItensAbertos(): void {
    this.itens.clear();
    this.falhou.clear();
    for (const c of this.contas ?? []) {
      if (this.abertas.has(c.nrInternoConta)) void this.carregarItens(c);
    }
  }

  private async carregarItens(c: ContaPacienteResumo): Promise<void> {
    const k = c.nrInternoConta;
    if (this.itens.has(k) || this.carregando.has(k)) return;
    this.carregando.add(k);
    this.falhou.delete(k);
    try {
      this.itens.set(k, (await this.atendimentoService.getItensContaPaciente(k)) ?? []);
    } catch {
      this.falhou.add(k);
    } finally {
      this.carregando.delete(k);
    }
  }

  private aplicarDestaque(): void {
    this.naoEncontrada = false;
    if (this.destacar == null) return;
    const conta = (this.contas ?? []).find(c => c.nrAtendimento === this.destacar);
    if (!conta) { this.naoEncontrada = true; return; }
    this.destaquePendente = false;
    if (this.filtro !== 'todos') {
      this.filtro = 'todos';
      // fora do ciclo de deteccao: o pai ja desenhou os chips nesta passada
      Promise.resolve().then(() => this.filtroChange.emit('todos'));
    }
    if (this.janela.ocultas.includes(conta)) this.mostrarOcultas = true;
    this.abertas.add(conta.nrInternoConta);
    void this.carregarItens(conta);
    this.contaDestacada = conta.nrInternoConta;
    if (this.timer) clearTimeout(this.timer);
    this.timer = setTimeout(() => { this.contaDestacada = null; }, DESTAQUE_MS);
    // a linha só existe no DOM depois da próxima detecção de mudanças
    setTimeout(() => this.host?.nativeElement?.querySelector?.(`[data-conta="${conta.nrInternoConta}"]`)?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }));
  }

  abrirMenu(ev: MouseEvent, conta: ContaPacienteResumo): void {
    this.menu.emit({ ev, conta });
  }
}
